import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Field, Badge, SectionTitle } from "@/components/ui-hud";
import BackToCockpit from "@/components/BackToCockpit";
import PasswordInput from "@/components/PasswordInput";
import { CreditCard, CalendarClock, ShieldOff, Trash2, AlertTriangle, Loader2, ArrowRight, RotateCcw } from "lucide-react";

/**
 * Account Settings — subscription + danger-zone.
 *
 * Sections:
 *   - Subscription card: current status, tier, enrollment date, renewal date,
 *     cancel-at-period-end toggle, reactivate.
 *   - Delete account: full confirm (retype email + password) → DELETE /api/users/me.
 */
export default function AccountSettings() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/billing/subscription").then((r) => setSub(r.data)).catch(() => setSub({}));
  }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center text-white/40"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const active = sub?.membership_status === "active";
  const cancelPending = !!sub?.subscription_cancel_at_period_end;
  const renewalDate = sub?.subscription_current_period_end ? new Date(sub.subscription_current_period_end) : null;
  const enrolledDate = sub?.subscription_started_at ? new Date(sub.subscription_started_at) : null;

  const cancel = async () => {
    if (!window.confirm(`Cancel your PJP membership? You'll keep full access until ${renewalDate ? renewalDate.toLocaleDateString() : "the end of your current billing period"}, then it will not renew.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/billing/cancel");
      setSub((s) => ({ ...s, subscription_cancel_at_period_end: true, subscription_current_period_end: r.data.access_until || s?.subscription_current_period_end }));
      toast.success("Cancellation scheduled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/billing/reactivate");
      setSub((s) => ({ ...s, subscription_cancel_at_period_end: false }));
      toast.success("Subscription reactivated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reactivate failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 space-y-8" data-testid="account-settings">
      <BackToCockpit />
      <SectionTitle overline="// ACCOUNT" title="Account settings" subtitle="Manage your subscription and personal data." />

      {/* Identity */}
      <Card testId="account-identity">
        <div className="overline text-cyan mb-2">// IDENTITY</div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-xl font-bold">{user.first_name} {user.last_name}</div>
            <div className="mono text-xs text-white/50">{user.email}</div>
          </div>
          <Link to="/profile" className="btn"><ArrowRight className="h-3.5 w-3.5" /> Edit profile</Link>
        </div>
      </Card>

      {/* Subscription */}
      <Card testId="account-subscription">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-cyan" />
            <div className="overline text-cyan">// SUBSCRIPTION</div>
          </div>
          <Badge tone={active ? "avail" : "neutral"}>{sub?.membership_status || "none"}</Badge>
        </div>

        {sub === null ? (
          <div className="text-white/50 text-sm"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-2" /> Loading…</div>
        ) : !active ? (
          <div className="rounded-lg border border-dashed border-white/10 bg-white/[0.02] p-5 text-center">
            <div className="font-display text-lg font-semibold mb-1">No active membership.</div>
            <div className="text-sm text-white/55 mb-4">Unlock the gated directory, PDF exports, priority AOG alerts, and more.</div>
            <Link to="/membership" data-testid="account-choose-plan" className="btn btn-primary inline-flex">Choose a plan <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoRow icon={CalendarClock} label="Enrolled" value={enrolledDate ? enrolledDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—"} />
              <InfoRow
                icon={CalendarClock}
                label={cancelPending ? "Access ends" : "Auto-renews"}
                value={renewalDate ? renewalDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—"}
                tone={cancelPending ? "orange" : "cyan"}
              />
              <InfoRow icon={CreditCard} label="Tier" value={(sub?.membership_tier_slug || "—").toUpperCase()} />
              <InfoRow
                icon={CreditCard}
                label="Amount"
                value={
                  sub?.subscription_price_amount != null
                    ? `${(sub.subscription_price_amount / 100).toLocaleString(undefined, { style: "currency", currency: (sub.subscription_price_currency || "usd").toUpperCase() })} / year`
                    : "—"
                }
              />
            </div>

            {cancelPending ? (
              <div className="rounded-lg border border-orange-hud/40 bg-orange-hud/[0.05] p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="cancel-pending-banner">
                <div>
                  <div className="mono text-[11px] text-orange-hud tracking-widest mb-1">CANCELLATION SCHEDULED</div>
                  <div className="text-sm text-white/80">
                    Your membership will not renew. You keep full access until{" "}
                    <span className="mono text-orange-hud">{renewalDate ? renewalDate.toLocaleDateString() : "period end"}</span>.
                  </div>
                </div>
                <button onClick={reactivate} disabled={busy} data-testid="account-reactivate" className="btn btn-primary">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5" /> Reactivate</>}
                </button>
              </div>
            ) : sub?.stripe_subscription_id ? (
              <div className="pt-3 border-t border-white/[0.06]">
                <button
                  onClick={cancel}
                  disabled={busy}
                  data-testid="account-cancel-subscription"
                  className="inline-flex items-center gap-2 mono text-[11px] tracking-widest text-orange-hud hover:text-orange-300 disabled:opacity-40"
                >
                  <ShieldOff className="h-3.5 w-3.5" />
                  {busy ? "Working…" : "Cancel subscription"}
                </button>
                <div className="text-[11px] text-white/40 mt-1.5">
                  Access continues until the end of the current billing period. No prorated refund.
                </div>
              </div>
            ) : (
              <div className="pt-3 border-t border-white/[0.06] text-[11px] text-white/50 mono" data-testid="sub-no-stripe">
                This membership is managed by PJP Admin — <Link to="/contact" className="text-cyan hover:underline">contact support</Link> to change it.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Danger zone */}
      <DangerZone user={user} onDeleted={async () => { await logout(); nav("/"); }} />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, tone = "cyan" }) {
  const color = tone === "orange" ? "text-orange-hud" : "text-cyan";
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-3 flex items-center gap-3">
      <Icon className={`h-4 w-4 ${color}`} />
      <div>
        <div className="overline text-white/45">{label}</div>
        <div className="mono text-sm text-white/90 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function DangerZone({ user, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const canDelete = confirmEmail.trim().toLowerCase() === (user.email || "").toLowerCase();

  const del = async () => {
    if (!canDelete) return;
    if (!window.confirm("Final confirmation: this permanently deletes your PJP account and all data. This cannot be undone. Continue?")) return;
    setBusy(true);
    try {
      await api.delete("/users/me", { data: { confirm_email: confirmEmail, password: password || undefined } });
      toast.success("Account deleted. Farewell — blue skies.");
      onDeleted && (await onDeleted());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-red-500/30" testId="account-danger-zone">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <div className="overline text-red-400">// DANGER ZONE</div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="account-open-delete" className="mono text-[11px] tracking-widest text-red-400 hover:text-red-300">
            <Trash2 className="h-3 w-3 inline mr-1" /> Delete my account
          </button>
        )}
      </div>

      {!open ? (
        <p className="text-sm text-white/60">
          Permanently deletes your PJP account and all data — profile, aircraft, marketplace listings,
          contracts, applications, forum activity, documents, payment history. Your active subscription
          will be cancelled immediately with no refund.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-500/40 bg-red-500/[0.05] p-3 text-[12px] text-white/80">
            This cannot be undone. Your data will be purged from every collection.
          </div>
          <Field label={<>Type <span className="mono text-red-400">{user.email}</span> to confirm</>}>
            <input
              data-testid="account-delete-email"
              className="w-full bg-white/[0.03] border border-red-500/40 rounded-lg px-3 py-2 text-sm text-white font-mono"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              autoFocus
            />
          </Field>
          {!user.oauth_provider && (
            <Field label="Password (extra confirmation)">
              <PasswordInput data-testid="account-delete-password" className="input-hud" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => { setOpen(false); setConfirmEmail(""); setPassword(""); }} className="btn" data-testid="account-delete-cancel">Cancel</button>
            <button
              onClick={del}
              disabled={!canDelete || busy}
              data-testid="account-delete-confirm-btn"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Permanently delete my account
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
